/**
 * Pure label resolvers for the office dashboard job cards (OFFICE-CARDS
 * Phase 1, PM-reported 2026-09-20).
 *
 * The office job lists come from `GET /api/v1/jobs`, which returns FLAT rows —
 * `driverId` / `truckId` only, never the nested `driver` / `truck` relations
 * (the detail routes attach those via `buildJobDetail`; the list route maps
 * with `toWireJob` alone — the `const wireJobs = rows.map((r) =>
 * Job.parse(toWireJob(...)))` statement in `GET /jobs`). The dashboard
 * therefore resolves the display labels from the driver + truck rosters it
 * fetches alongside the lists. These helpers own that fallback ladder so each
 * card branch is one tested function instead of an inline ternary that
 * silently reads an always-undefined field.
 *
 * Ladder (driver):
 *   id null/undefined            → the caller's unassigned text
 *                                  ("Unassigned" on the Active tab, "—" on the
 *                                  Review Queue) — an office-created job that
 *                                  was never assigned.
 *   id present, roster not yet   → ROSTER_LOADING_TEXT ("…"). Never flash
 *   loaded                        "Unassigned" on a job that IS assigned: the
 *                                  roster lands async and a transient false
 *                                  label is worse than a pending ellipsis.
 *   id present, roster loaded,   → "Unknown driver" (the driver row was
 *   id missing from roster        hard-deleted; render something sane).
 *   id present, hit              → the driver's display name.
 *
 * Ladder (truck): mirror image — null → "No Truck"; pending → "…"; miss →
 * "?"; hit → "Truck <unitNumber>".
 */

/** Shown while the roster fetch is still in flight and the id is known. */
export const ROSTER_LOADING_TEXT = "…";

/** Text for an id that is set but absent from a loaded roster (row deleted). */
export const DRIVER_UNKNOWN_TEXT = "Unknown driver";

/**
 * Driver label for the Active Jobs tab: an id-less job reads "Unassigned".
 * `names` is the driver roster (`null` until the fetch resolves).
 */
export function resolveDriverLabel(
  driverId: string | null | undefined,
  names: Map<string, string> | null,
): string {
  if (driverId === null || driverId === undefined) return "Unassigned";
  if (names === null) return ROSTER_LOADING_TEXT;
  return names.get(driverId) ?? DRIVER_UNKNOWN_TEXT;
}

/**
 * Driver label for the Review Queue: an id-less job reads "—" (existing
 * semantics; a finished job without a driver renders a dash, not a word).
 */
export function resolveDriverLabelDash(
  driverId: string | null | undefined,
  names: Map<string, string> | null,
): string {
  if (driverId === null || driverId === undefined) return "—";
  if (names === null) return ROSTER_LOADING_TEXT;
  return names.get(driverId) ?? DRIVER_UNKNOWN_TEXT;
}

/**
 * Truck label for the Active Jobs tab: an id-less job reads "No Truck".
 * `units` maps truck id → unit number (`null` until the fetch resolves).
 */
export function resolveTruckLabel(
  truckId: string | null | undefined,
  units: Map<string, number> | null,
): string {
  if (truckId === null || truckId === undefined) return "No Truck";
  if (units === null) return ROSTER_LOADING_TEXT;
  const unit = units.get(truckId);
  return unit === undefined ? "?" : `Truck ${unit}`;
}
